/**
 * HUD Right Column — Buildings 3×3, Inventory 2×2, Gem Actions 2×3.
 */

import type { GameStateView } from "../../application/ports.js";
import { t, onLocaleChange } from "../../i18n/index.js";
import { openGemCreateModal } from "./gem-create-modal.js";
import { attachLongPressTooltip } from "./long-press-tooltip.js";

export interface RightColumnCallbacks {
  onPlacementModeChange?: (mode: "Tower" | "Wall" | "Amplifier" | "Trap" | "Shrine") => void;
  onGemCreate?: (gemType: string) => void;
  onSanctify?: () => void;
  onReplicate?: () => void;
  onDuplicate?: () => void;
  onCombine?: () => void;
  onMeltdown?: () => void;
  onInventoryDragStart?: (params: { gemId: string; screenX: number; screenY: number }) => void;
  /** Called when a player clicks (taps without drag) an inventory gem, for combine selection. */
  onInventoryGemClick?: (gemId: string) => void;
  /** Called when a player drags one inventory gem onto another inventory gem. */
  onInventoryGemDrop?: (sourceGemId: string, targetGemId: string) => void;
}

// Gem color palette (matches canvas-render-adapter GEM_COLOR but thematic)
const GEM_COLOR: Record<string, string> = {
  Yellow: "#ffe55a",
  Orange: "#ff8c20",
  Red: "#d63030",
  Purple: "#a45ae5",
  Green: "#30c830",
  Blue: "#4a8dc4",
  Cyan: "#5acff0",
  Lime: "#a8e030",
};

const BUILDINGS_ACTIVE: Array<{
  key: "Tower" | "Wall" | "Amplifier" | "Trap" | "Shrine";
  icon: string;
  nameKey: string;
  costKey: keyof GameStateView;
}> = [
  { key: "Tower",     icon: "⛪", nameKey: "hud.buildChapel",    costKey: "towerCost"     },
  { key: "Wall",      icon: "🧱", nameKey: "hud.buildWall",      costKey: "wallCost"      },
  { key: "Amplifier", icon: "🧎", nameKey: "hud.buildAmplifier", costKey: "amplifierCost" },
  { key: "Trap",      icon: "☀", nameKey: "hud.buildTrap",      costKey: "trapCost"      },
  { key: "Shrine",    icon: "⛲", nameKey: "hud.buildShrine",    costKey: "shrineCost"    },
];
const INV_SLOTS = 4; // B16: shrunk 3×4 → 2×2

export function createRightColumn(parent: HTMLElement, cbs: RightColumnCallbacks): {
  update(state: GameStateView): void;
} {
  const root = document.createElement("div");
  root.className = "hud-right";

  // Singleton tooltip attached to <body> so it's never clipped by panel overflow.
  const tooltip = document.createElement("div");
  tooltip.className = "hud-relic-tooltip";
  document.body.appendChild(tooltip);

  function showTip(anchor: HTMLElement, text: string): void {
    tooltip.textContent = text;
    tooltip.classList.add("hud-relic-tooltip--visible");
    const r = anchor.getBoundingClientRect();
    const tw = tooltip.offsetWidth;
    const th = tooltip.offsetHeight;
    // Prefer to the left of the slot (right column sits on screen edge).
    let x = r.left - tw - 8;
    if (x < 4) x = r.right + 8;
    let y = r.top + r.height / 2 - th / 2;
    if (y < 4) y = 4;
    if (y + th > window.innerHeight - 4) y = window.innerHeight - th - 4;
    tooltip.style.left = `${x}px`;
    tooltip.style.top = `${y}px`;
  }

  function hideTip(): void {
    tooltip.classList.remove("hud-relic-tooltip--visible");
  }

  // ── Buildings panel ──────────────────────────────────────────────────────
  const buildingsPanel = document.createElement("div");
  buildingsPanel.className = "hud-right__buildings";

  const buildingsHeader = document.createElement("div");
  buildingsHeader.className = "hud-right__panel-header";
  const buildingsTitle = document.createElement("span");
  buildingsTitle.className = "hud-right__panel-title";
  buildingsTitle.textContent = t("hud.buildings");
  buildingsHeader.appendChild(buildingsTitle);

  const buildingsGrid = document.createElement("div");
  buildingsGrid.className = "hud-right__buildings-grid";

  // Active building slots
  const activeBuildingSlots: Array<{
    el: HTMLDivElement;
    nameEl: HTMLSpanElement;
    costEl: HTMLSpanElement;
  }> = [];
  for (const bd of BUILDINGS_ACTIVE) {
    const slot = document.createElement("div");
    slot.className = "hud-right__buildings-slot";
    slot.addEventListener("click", () => cbs.onPlacementModeChange?.(bd.key));

    const icon = document.createElement("span");
    icon.className = "hud-right__buildings-slot-icon";
    icon.textContent = bd.icon;

    const name = document.createElement("span");
    name.className = "hud-right__buildings-slot-name";
    name.textContent = t(bd.nameKey);

    const cost = document.createElement("span");
    cost.className = "hud-right__buildings-slot-cost";
    cost.textContent = "...";

    slot.appendChild(icon);
    slot.appendChild(name);
    slot.appendChild(cost);
    buildingsGrid.appendChild(slot);
    activeBuildingSlots.push({ el: slot, nameEl: name, costEl: cost });
  }

  buildingsPanel.appendChild(buildingsHeader);
  buildingsPanel.appendChild(buildingsGrid);

  // ── Inventory panel ──────────────────────────────────────────────────────
  const inventoryPanel = document.createElement("div");
  inventoryPanel.className = "hud-right__inventory";

  const invHeader = document.createElement("div");
  invHeader.className = "hud-right__panel-header";
  const invTitle = document.createElement("span");
  invTitle.className = "hud-right__panel-title";
  invTitle.textContent = t("hud.relics");
  const invMeta = document.createElement("span");
  invMeta.className = "hud-right__panel-meta";
  invMeta.textContent = t("hud.relicsCount", { filled: "0", total: INV_SLOTS });
  invHeader.appendChild(invTitle);
  invHeader.appendChild(invMeta);

  const invGrid = document.createElement("div");
  invGrid.className = "hud-right__inventory-grid";

  const invSlots: HTMLDivElement[] = [];
  for (let i = 0; i < INV_SLOTS; i++) {
    const slot = document.createElement("div");
    slot.className = "hud-right__inventory-slot";
    invGrid.appendChild(slot);
    invSlots.push(slot);
  }

  inventoryPanel.appendChild(invHeader);
  inventoryPanel.appendChild(invGrid);

  // ── Actions panel ────────────────────────────────────────────────────────
  const actionsPanel = document.createElement("div");
  actionsPanel.className = "hud-right__actions";

  const actionsHeader = document.createElement("div");
  actionsHeader.className = "hud-right__panel-header";
  const actionsTitle = document.createElement("span");
  actionsTitle.className = "hud-right__panel-title";
  actionsTitle.textContent = t("hud.actions");
  const actionsMeta = document.createElement("span");
  actionsMeta.className = "hud-right__panel-meta";
  actionsMeta.textContent = t("hud.actionsHint");
  actionsHeader.appendChild(actionsTitle);
  actionsHeader.appendChild(actionsMeta);

  const actionsGrid = document.createElement("div");
  actionsGrid.className = "hud-right__actions-grid";

  // Create button (opens gem type selection modal on click, keyboard 1-8 still works)
  const createBtn = document.createElement("button");
  createBtn.type = "button";
  createBtn.className = "hud-right__action-btn";
  const createIcon = document.createElement("span");
  createIcon.className = "hud-right__action-icon";
  createIcon.textContent = "✦";
  const createLabel = document.createElement("span");
  createLabel.className = "hud-right__action-label";
  createLabel.textContent = t("hud.actionCreate");
  const createCost = document.createElement("span");
  createCost.className = "hud-right__action-cost";
  createCost.textContent = "...";

  const paletteEl = document.createElement("div");
  paletteEl.style.cssText = "display:flex; gap:2px; margin-top:2px;";
  const GEM_KEYS = [
    { k:"1", c:GEM_COLOR.Yellow, type:"Yellow" },
    { k:"2", c:GEM_COLOR.Orange, type:"Orange" },
    { k:"3", c:GEM_COLOR.Red,    type:"Red" },
    { k:"4", c:GEM_COLOR.Purple, type:"Purple" },
    { k:"5", c:GEM_COLOR.Green,  type:"Green" },
    { k:"6", c:GEM_COLOR.Blue,   type:"Blue" },
    { k:"7", c:GEM_COLOR.Cyan,   type:"Cyan" },
    { k:"8", c:GEM_COLOR.Lime,   type:"Lime" },
  ];
  for (const gk of GEM_KEYS) {
    const sw = document.createElement("div");
    sw.style.cssText = `width:10px; height:10px; background:${gk.c}; color:#000; font-size:8px; display:flex; align-items:center; justify-content:center; font-weight:bold; border-radius:1px;`;
    sw.textContent = gk.k;
    paletteEl.appendChild(sw);
  }

  createBtn.appendChild(createIcon);
  createBtn.appendChild(createLabel);
  createBtn.appendChild(paletteEl);
  createBtn.appendChild(createCost);
  createBtn.title = t("hud.createGemHint");

  // Track current allowed gem types for the modal
  let _allowedGemTypes: string[] | undefined;

  // Create button opens gem-create modal on click
  createBtn.addEventListener("click", () => {
    openGemCreateModal({
      anchor: createBtn,
      allowedTypes: _allowedGemTypes,
      onSelect: (gemType) => { cbs.onGemCreate?.(gemType); },
    });
  });

  // Dual button
  const dualBtn = createActionButton("✧", t("hud.actionDual"), "[D]", () => {
    cbs.onDuplicate?.();
  });

  // Combine button
  const combineBtn = createActionButton("⬡", t("hud.actionCombine"), "[C]", () => {
    cbs.onCombine?.();
  });

  // Sanctify button
  const sanctifyBtn = createActionButton("⇧", t("hud.actionSanctify"), "[U]", () => {
    cbs.onSanctify?.();
  });

  // Bomb button
  const bombBtn = createActionButton("💥", t("hud.actionBomb"), "[drag]", () => {
    console.log("[HUD] bomb — drag gem onto field");
  });

  // Melt button
  const meltBtn = createActionButton("♺", t("hud.actionMelt"), "[X]", () => {
    cbs.onMeltdown?.();
  });

  // Random (locked)
  const randomBtn = createActionButton("🎲", t("hud.actionRandom"), "🔒", null);
  randomBtn.disabled = true;
  randomBtn.classList.add("hud-right__action-btn--locked");

  actionsGrid.appendChild(createBtn);
  actionsGrid.appendChild(dualBtn);
  actionsGrid.appendChild(combineBtn);
  actionsGrid.appendChild(sanctifyBtn);
  actionsGrid.appendChild(bombBtn);
  actionsGrid.appendChild(meltBtn);
  actionsGrid.appendChild(randomBtn);

  actionsPanel.appendChild(actionsHeader);
  actionsPanel.appendChild(actionsGrid);

  // Mount all panels
  root.appendChild(buildingsPanel);
  root.appendChild(inventoryPanel);
  root.appendChild(actionsPanel);
  parent.appendChild(root);

  // Refresh all static i18n labels (called on locale change)
  function refreshLabels(): void {
    buildingsTitle.textContent = t("hud.buildings");
    invTitle.textContent = t("hud.relics");
    actionsTitle.textContent = t("hud.actions");
    actionsMeta.textContent = t("hud.actionsHint");
    createLabel.textContent = t("hud.actionCreate");
    createBtn.title = t("hud.createGemHint");
    for (let i = 0; i < BUILDINGS_ACTIVE.length; i++) {
      activeBuildingSlots[i].nameEl.textContent = t(BUILDINGS_ACTIVE[i].nameKey);
    }
  }
  // Subscribe to locale changes so building labels update mid-battle.
  onLocaleChange(refreshLabels);

  // State tracking
  let _lastPlacementMode: string = "Tower";
  let _lastInventoryJson = "";
  let _lastCosts = "";
  let _lastGemCreateCost = -1;
  let inventoryDragGemId: string | null = null;
  let inventoryDragMoved = false;

  return {
    update(state: GameStateView): void {
      // ── Track allowed gem types for modal ───────────────────────────────
      _allowedGemTypes = (state as GameStateView & { allowedGemTypes?: string[] }).allowedGemTypes;

      // ── Building slots placement mode + costs ────────────────────────────
      const pm = state.placementMode ?? "Tower";
      const costValues = [
        state.towerCost,
        state.wallCost,
        state.amplifierCost,
        state.trapCost,
        state.shrineCost,
      ];
      const costsStr = costValues.join(",");

      if (pm !== _lastPlacementMode || costsStr !== _lastCosts) {
        _lastPlacementMode = pm;
        _lastCosts = costsStr;
        for (let i = 0; i < BUILDINGS_ACTIVE.length; i++) {
          const { el, costEl } = activeBuildingSlots[i];
          const bd = BUILDINGS_ACTIVE[i];
          const isActive = pm === bd.key;
          if (isActive) {
            el.classList.add("hud-right__buildings-slot--active");
          } else {
            el.classList.remove("hud-right__buildings-slot--active");
          }
          const cost = costValues[i];
          costEl.textContent = cost !== undefined ? String(cost) : "—";
        }
      }

      // ── Inventory slots ──────────────────────────────────────────────────
      const gems = state.gemInventory ?? [];
      const invJson = JSON.stringify(gems.map(g => `${g.id}:${g.type}:${g.level}:${g.secondaryType ?? ""}`));
      if (invJson !== _lastInventoryJson) {
        _lastInventoryJson = invJson;
        // Update meta
        invMeta.textContent = t("hud.relicsCount", { filled: String(gems.length), total: String(INV_SLOTS) });
        // Rebuild slots
        for (let i = 0; i < INV_SLOTS; i++) {
          const slot = invSlots[i];
          slot.innerHTML = "";
          slot.className = "hud-right__inventory-slot";
          if (i < gems.length) {
            const gem = gems[i];
            const color = GEM_COLOR[gem.type] ?? "#666666";
            slot.classList.add("hud-right__inventory-slot--filled");
            const gemEl = document.createElement("div");
            gemEl.className = "hud-right__inventory-slot-gem";
            gemEl.style.color = color; // exposes gem color as currentColor for CSS inset glow
            gemEl.innerHTML = getRelicSvg(gem.type, gem.level, color);

            // Dual-type indicator: small colored dot for secondary gem type
            if (gem.secondaryType) {
              const secColor = GEM_COLOR[gem.secondaryType] ?? "#888888";
              const dotEl = document.createElement("span");
              dotEl.className = "hud-right__inventory-slot-secondary";
              dotEl.style.background = secColor;
              gemEl.appendChild(dotEl);
            }
            slot.appendChild(gemEl);
            // Drag handling + combine selection
            const gemId = gem.id;
            let _dragMoved = false;
            slot.addEventListener("pointerdown", (e: PointerEvent) => {
              _dragMoved = false;
              inventoryDragGemId = gemId;
              inventoryDragMoved = false;
              e.preventDefault();
              hideTip();
              cbs.onInventoryDragStart?.({
                gemId,
                screenX: e.clientX,
                screenY: e.clientY,
              });
            }, { once: false });
            slot.addEventListener("pointermove", () => {
              _dragMoved = true;
              inventoryDragMoved = true;
            }, { once: false });
            slot.addEventListener("pointerup", () => {
              if (inventoryDragMoved && inventoryDragGemId && inventoryDragGemId !== gemId) {
                cbs.onInventoryGemDrop?.(inventoryDragGemId, gemId);
              } else if (!_dragMoved) {
                cbs.onInventoryGemClick?.(gemId);
              }
              _dragMoved = false;
              inventoryDragGemId = null;
              inventoryDragMoved = false;
            }, { once: false });

            // Hover tooltip with preview stats.
            // Desktop: mouseenter/leave; Touch (B12): long-press via helper.
            if (gem.stats) {
              const s = gem.stats;
              const lines = [
                `${s.specialEffect ?? gem.type} · ${t("gem.level", { lvl: gem.level })}`,
                t("hud.damage", { min: s.damageMin, max: s.damageMax }),
                t("hud.range", { range: s.range.toFixed(1) }),
                t("hud.fireRate", { rate: (1000 / s.fireRateMs).toFixed(2) }),
              ];
              for (const detail of s.effectDetails ?? []) {
                lines.push(detail);
              }
              if (s.upgradeCost !== undefined) {
                lines.push(t("hud.upgradeCost", { cost: s.upgradeCost }));
              }
              const tipText = lines.join("\n");
              slot.addEventListener("mouseenter", () => showTip(slot, tipText));
              slot.addEventListener("mouseleave", hideTip);
              // Touch long-press shows the same tooltip via callbacks.
              attachLongPressTooltip(slot, {
                onOpen: () => showTip(slot, tipText),
                onClose: hideTip,
              });
            }
          }
        }
      }

      // ── Gem create cost ──────────────────────────────────────────────────
      const gcc = state.gemCreateCost ?? 77;
      if (gcc !== _lastGemCreateCost) {
        _lastGemCreateCost = gcc;
        createCost.textContent = `${gcc} Маны`;
      }
    },
  };
}

function createActionButton(
  icon: string,
  label: string,
  costStr: string,
  onClick: (() => void) | null
): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "hud-right__action-btn";
  const iconEl = document.createElement("span");
  iconEl.className = "hud-right__action-icon";
  iconEl.textContent = icon;
  const labelEl = document.createElement("span");
  labelEl.className = "hud-right__action-label";
  labelEl.textContent = label;
  const costEl = document.createElement("span");
  costEl.className = "hud-right__action-cost";
  costEl.textContent = costStr;
  btn.appendChild(iconEl);
  btn.appendChild(labelEl);
  btn.appendChild(costEl);
  if (onClick) {
    btn.addEventListener("click", onClick);
  }
  return btn;
}

function getRelicSvg(type: string, level: number, color: string): string {
  let path = "";
  switch (type) {
    case "Yellow": path = "M50,5 L61,35 L95,35 L68,57 L79,91 L50,70 L21,91 L32,57 L5,35 L39,35 Z"; break; // Star
    case "Orange": path = "M60,5 L20,55 L45,55 L35,95 L75,45 L50,45 Z"; break; // Lightning
    case "Red":    path = "M50,90 C50,90 10,60 10,35 A20,20 0 0,1 50,35 A20,20 0 0,1 90,35 C90,60 50,90 50,90 Z"; break; // Heart
    case "Purple": path = "M50,10 L90,50 L50,90 L10,50 Z"; break; // Diamond
    case "Green":  path = "M50,95 C10,95 10,60 50,5 C65,30 65,60 50,95 Z"; break; // Flame
    case "Blue":   path = "M50,10 L85,30 L85,70 L50,90 L15,70 L15,30 Z M15,30 L85,70 M15,70 L85,30 M50,10 L50,90"; break; // Crystal
    case "Cyan":   path = "M50,10 A40,40 0 1,1 10,50"; break; // Swirl
    case "Lime":   path = "M50,90 Q90,90 90,50 Q90,10 50,10 Q10,10 10,50 Q10,90 50,90 Z M50,90 L50,10"; break; // Leaf
    default:       path = "M50,50 m-40,0 a40,40 0 1,0 80,0 a40,40 0 1,0 -80,0";
  }

  let dots = "";
  if (level > 1) {
    const count = level - 1;
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2 - Math.PI / 2;
      const x = 50 + Math.cos(angle) * 42;
      const y = 50 + Math.sin(angle) * 42;
      dots += `<circle cx="${x}" cy="${y}" r="6" fill="white" />`;
    }
  }

  return `
    <svg viewBox="0 0 100 100" style="width:100%; height:100%;">
      <path d="${path}" fill="${color}" stroke="white" stroke-width="4" stroke-linejoin="round" />
      ${dots}
    </svg>
  `;
}
